import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { BiEdit, BiTrash, BiShow } from "react-icons/bi";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  DocumentArrowDownIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArchiveBoxXMarkIcon,
} from "@heroicons/react/24/solid";
import {
  CubeIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getAllAssets, createAsset, deleteAsset } from "../../Api/assets";

/* ─────────── helpers ─────────── */
const FILE_BASE_URL = "https://api2.eduforestin.com";

const getFileUrl = (value) =>
  value ? `${FILE_BASE_URL}/${value}` : "";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtCurrency = (n) =>
  n != null
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

/* ─────────── status config ─────────── */
const STATUS_CONFIG = {
  Active: {
    bg: "bg-green-100",
    text: "text-green-700",
    icon: <ShieldCheckIcon className="w-3.5 h-3.5" />,
  },
  "In Repair": {
    bg: "bg-orange-100",
    text: "text-orange-700",
    icon: <WrenchScrewdriverIcon className="w-3.5 h-3.5" />,
  },
  Damaged: {
    bg: "bg-red-100",
    text: "text-red-700",
    icon: <ExclamationTriangleIcon className="w-3.5 h-3.5" />,
  },
  Disposed: {
    bg: "bg-gray-200",
    text: "text-gray-600",
    icon: <ArchiveBoxXMarkIcon className="w-3.5 h-3.5" />,
  },
};

const StatusBadge = ({ value, label }) => {
  const cfg = STATUS_CONFIG[value] ?? {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: null,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}
    >
      {cfg.icon}
      {label || value || "—"}
    </span>
  );
};

/* ─────────── empty form ─────────── */
const emptyForm = {
  name: "",
  type: "",
  location: "",
  assetType: "",
  clientName: "",
  brand: "",
  model: "",
  serialNumber: "",
  quantity: "",
  price: "",
  totalCost: "",
  vendor: "",
  purchaseDate: "",
  warrantyStart: "",
  warrantyEnd: "",
  status: "",
  bill: null,
};

const fieldLabels = {
  name: "Asset Name",
  type: "Type",
  location: "Location",
  assetType: "Asset Type",
  clientName: "Client Name",
  brand: "Brand",
  model: "Model",
  serialNumber: "Serial Number",
  quantity: "Quantity",
  price: "Unit Price (₹)",
  totalCost: "Total Cost (₹)",
  vendor: "Vendor",
  purchaseDate: "Purchase Date",
  warrantyStart: "Warranty Start",
  warrantyEnd: "Warranty End",
  status: "Status",
  bill: "Bill / Invoice",
};

const requiredFields = ["name", "type", "location", "assetType", "quantity"];

/* ═══════════════════════════════════════════════════════════ */
const AssetsManagement = () => {
  const [data, setData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  const [openDrawer, setOpenDrawer] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* ─── load ─── */
  const loadAssets = async () => {
    try {
      setLoadingTable(true);
      const res = await getAllAssets();
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error("Failed to load assets");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  /* ─── filtered data ─── */
  const filtered = data.filter((a) => {
    const q = search.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.type?.toLowerCase().includes(q) ||
      a.location?.toLowerCase().includes(q) ||
      a.brand?.toLowerCase().includes(q) ||
      a.serialNumber?.toLowerCase().includes(q)
    );
  });

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ─── form handlers ─── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "assetType" && value !== "Client" ? { clientName: "" } : {}),
    }));
  };

  const handleFile = (e) => {
    setForm((prev) => ({ ...prev, bill: e.target.files[0] }));
  };

  const validateForm = () => {
    for (const key of requiredFields) {
      if (!form[key]?.toString().trim()) {
        toast.error(`Please fill ${fieldLabels[key]}`);
        return false;
      }
    }
    if (form.assetType === "Client" && !form.clientName?.trim()) {
      toast.error("Please fill Client Name");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      toast.loading("Saving asset...", { id: "save-asset" });

      const fd = new FormData();
      Object.keys(form).forEach((key) => {
        if (form[key] !== null && form[key] !== "") fd.append(key, form[key]);
      });

      await createAsset(fd);
      toast.success("Asset created", { id: "save-asset" });
      setOpenDrawer(false);
      setForm(emptyForm);
      loadAssets();
    } catch {
      toast.error("Save failed", { id: "save-asset" });
    } finally {
      setSaving(false);
    }
  };

  /* ─── delete ─── */
  const askDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const performDelete = async () => {
    try {
      await deleteAsset(deleteId);
      toast.success("Deleted");
      setDeleteDialogOpen(false);
      loadAssets();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ─────────────────────────────────── RENDER ─────────────────────────────────── */
  return (
    <Box p={3}>
      {/* ══════════ TOP BAR ══════════ */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* Search */}
        <div className="relative w-full max-w-xl">
          <div className="relative flex items-center h-[56px] rounded-full bg-white/80 backdrop-blur-xl border border-gray-200 shadow-md">
            <span className="pl-5 text-gray-400">
              <MagnifyingGlassIcon className="w-5 h-5" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by Name, Type, Location, Brand…"
              className="flex-1 h-full bg-transparent px-4 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
            />
            {search && (
              <>
                <div className="h-6 w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => {
                    setSearch("");
                    setPage(0);
                  }}
                  className="mr-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </Box>

      {/* ══════════ ACTION ROW ══════════ */}
      <Box
        className="w-full flex items-center justify-between gap-4 mb-3 pb-2 overflow-x-auto scrollbar-hide"
      >
        {/* Left — stats chips */}
        <div className="flex gap-3 shrink-0">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600">
            <CubeIcon className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-indigo-600">{data.length}</span> Total Assets
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" />
            <span className="font-semibold text-green-600">
              {data.filter((d) => d.status === "Active").length}
            </span> Active
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600">
            <WrenchScrewdriverIcon className="w-4 h-4 text-orange-500" />
            <span className="font-semibold text-orange-600">
              {data.filter((d) => d.status === "In Repair").length}
            </span> In Repair
          </div>
        </div>

        {/* Right — actions */}
        <div className="flex gap-3 shrink-0 ml-auto">
          {/* Export — coming soon */}
          <div className="relative group">
            <button
              disabled
              className="flex items-center gap-2 h-[44px] px-5 rounded-xl border border-gray-300 bg-gray-100 text-gray-400 font-medium shadow-sm cursor-not-allowed whitespace-nowrap"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Export
            </button>
            <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1 rounded-md bg-gray-900 text-white text-xs opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
              Coming soon
            </div>
          </div>

          {/* Add Asset */}
          <button
            onClick={() => {
              setForm(emptyForm);
              setOpenDrawer(true);
            }}
            className="flex items-center gap-2 h-[44px] px-6 rounded-xl bg-emerald-600 text-white font-semibold shadow-md hover:bg-emerald-700 transition whitespace-nowrap"
          >
            <PlusIcon className="w-5 h-5" />
            Add Asset
          </button>
        </div>
      </Box>

      {/* ══════════ TABLE ══════════ */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{ background: "linear-gradient(to right, #eef2ff, #e0e7ff)" }}
            >
              <TableCell>#</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                Type / Brand
              </TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                Location
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Asset Type
              </TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                Qty / Cost
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Purchase Date
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Warranty
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingTable ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length ? (
              paginated.map((a, idx) => (
                <TableRow key={a._id} hover>
                  {/* # */}
                  <TableCell>
                    <strong>{page * rowsPerPage + idx + 1}</strong>
                  </TableCell>

                  {/* Asset */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center shadow shrink-0">
                        <span className="text-indigo-700 font-bold">
                          {a.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold">{a.name}</p>
                        <p className="text-xs text-gray-500 sm:hidden">{a.type}</p>
                        {a.serialNumber && (
                          <p className="text-xs text-gray-400">
                            S/N: {a.serialNumber}
                          </p>
                        )}
                      </div>
                    </Box>
                  </TableCell>

                  {/* Type / Brand */}
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    <p className="font-medium">{a.type}</p>
                    {a.brand && (
                      <p className="text-xs text-gray-500">
                        {a.brand} {a.model ? `· ${a.model}` : ""}
                      </p>
                    )}
                  </TableCell>

                  {/* Location */}
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BuildingOffice2Icon className="w-4 h-4 text-indigo-400 shrink-0" />
                      {a.location || "—"}
                    </Box>
                  </TableCell>

                  {/* Asset Type */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    {a.assetType === "Client"
                      ? `Client (${a.clientName || "—"})`
                      : a.assetType || "DBSkills"}
                  </TableCell>

                  {/* Qty / Cost */}
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <p className="font-semibold">{a.quantity}</p>
                    <p className="text-xs text-gray-500">{fmtCurrency(a.totalCost)}</p>
                  </TableCell>

                  {/* Purchase Date */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    {fmtDate(a.purchaseDate)}
                  </TableCell>

                  {/* Warranty */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    <StatusBadge value={a.warrantyStatus} />
                    {a.warrantyEnd && (
                      <p className="text-xs text-gray-400 mt-1">
                        Till {fmtDate(a.warrantyEnd)}
                      </p>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <StatusBadge value={a.status} />
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <button
                        onClick={() => {
                          setSelectedAsset(a);
                          setViewDialogOpen(true);
                        }}
                        className="p-2 rounded-lg bg-white shadow hover:bg-gray-100 transition"
                        title="View"
                      >
                        <BiShow size={18} className="text-blue-600" />
                      </button>
                      <button
                        className="p-2 rounded-lg bg-white shadow hover:bg-yellow-100 transition"
                        title="Edit"
                      >
                        <BiEdit size={18} className="text-yellow-600" />
                      </button>
                      <button
                        onClick={() => askDelete(a._id)}
                        className="p-2 rounded-lg bg-white shadow hover:bg-red-100 transition"
                        title="Delete"
                      >
                        <BiTrash size={18} className="text-red-600" />
                      </button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box py={6} textAlign="center">
                    <CubeIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <Typography color="text.secondary">
                      No assets found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* ══════════ CREATE DRAWER ══════════ */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "80%", md: "60%", lg: "50%", xl: "55%" },
            backgroundColor: "#ffffff",
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            p: 4,
            maxHeight: "100vh",
            overflowY: "auto",
            background: "#f9fafb",
          }}
        >
          {/* Header */}
          <Typography variant="h5" fontWeight="bold" mb={3} color="gray.800">
            Add New Asset
          </Typography>

          <Box
            sx={{
              p: 3,
              borderRadius: 3,
            }}
          >
            {/* ─── Section: Identity ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-3">
              Asset Identity
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {["name", "type", "brand", "model", "serialNumber", "location"].map((f) => (
                <div key={f} className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    {fieldLabels[f]}
                    {requiredFields.includes(f) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <input
                    name={f}
                    value={form[f]}
                    onChange={handleChange}
                    placeholder={fieldLabels[f]}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              ))}

              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.assetType}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="assetType"
                  value={form.assetType}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Select</option>
                  <option value="DBSkills">DBSkills</option>
                  <option value="Client">Client</option>
                </select>
              </div>

              {form.assetType === "Client" && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    {fieldLabels.clientName}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    name="clientName"
                    value={form.clientName}
                    onChange={handleChange}
                    placeholder={fieldLabels.clientName}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              )}
            </div>

            {/* ─── Section: Financials ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-500 mb-3">
              Financials & Procurement
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {/* Quantity */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.quantity}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  min={1}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Price */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.price}
                </label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Total Cost */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.totalCost}
                </label>
                <input
                  type="number"
                  name="totalCost"
                  value={form.totalCost}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Vendor */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.vendor}
                </label>
                <input
                  name="vendor"
                  value={form.vendor}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Purchase Date */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.purchaseDate}
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={form.purchaseDate}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            {/* ─── Section: Warranty & Status ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">
              Warranty & Status
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {/* Warranty Start */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.warrantyStart}
                </label>
                <input
                  type="date"
                  name="warrantyStart"
                  value={form.warrantyStart}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Warranty End */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.warrantyEnd}
                </label>
                <input
                  type="date"
                  name="warrantyEnd"
                  value={form.warrantyEnd}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.status}
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Select Status</option>
                  <option>Active</option>
                  <option>In Repair</option>
                  <option>Damaged</option>
                  <option>Disposed</option>
                </select>
              </div>
            </div>

            {/* ─── Section: Document ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
              Documents
            </p>
            <div className="flex flex-col mb-6">
              <label className="text-sm font-semibold text-gray-700 mb-1">
                Upload Bill / Invoice
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFile}
                className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {form.bill && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {form.bill.name}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpenDrawer(false)}
                sx={{ textTransform: "none", px: 4 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={saving}
                sx={{ textTransform: "none", px: 4 }}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  "Save Asset"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ══════════ VIEW DIALOG ══════════ */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Asset Details</DialogTitle>
        <DialogContent dividers>
          {selectedAsset && (
            <>
              {/* Status row */}
              <Box mb={3} display="flex" gap={2} flexWrap="wrap">
                <StatusBadge value={selectedAsset.status} />
                <StatusBadge
                  value={selectedAsset.warrantyStatus}
                  label={`Warranty: ${selectedAsset.warrantyStatus}`}
                />
              </Box>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.keys(emptyForm)
                  .filter((f) => f !== "bill")
                  .map((f) => (
                    <div key={f}>
                      <p className="text-sm text-gray-500">{fieldLabels[f]}</p>
                      <p className="font-semibold text-gray-800">
                        {["price", "totalCost"].includes(f)
                          ? fmtCurrency(selectedAsset[f])
                          : ["purchaseDate", "warrantyStart", "warrantyEnd"].includes(f)
                          ? fmtDate(selectedAsset[f])
                          : selectedAsset[f] || "—"}
                      </p>
                    </div>
                  ))}

                {/* Bill / Invoice */}
                <div className="col-span-2 md:col-span-3">
                  <p className="text-sm text-gray-500">Bill / Invoice</p>
                  {selectedAsset.bill ? (
                    <Box mt={1} display="flex" flexDirection="column" gap={1}>
                      <a
                        href={getFileUrl(selectedAsset.bill)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-indigo-600 underline text-sm"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        Open File
                      </a>
                      {/\.(jpg|jpeg|png|gif)$/i.test(selectedAsset.bill) && (
                        <img
                          src={getFileUrl(selectedAsset.bill)}
                          alt="bill preview"
                          className="w-48 h-auto rounded border mt-1"
                        />
                      )}
                    </Box>
                  ) : (
                    <p className="text-gray-400">—</p>
                  )}
                </div>

                {/* Timestamps */}
                <div>
                  <p className="text-sm text-gray-500">Created At</p>
                  <p className="font-semibold text-gray-800">
                    {selectedAsset.createdAt
                      ? new Date(selectedAsset.createdAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "—"}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ DELETE CONFIRM ══════════ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Asset?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={performDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetsManagement;
